<template>
  <card-canvas :class="{'view-only': isBuiltIn}" class="workflow-detail" size="big">
    <card-canvas-two-deep-breadcrumb
      :parent-route="RouteAssistant.getWorkflowsRoute($route.params.accountId)"
      :child-name="workflow.name"
      parent-name="Workflows" />
    <loading-placeholder v-if="isInitialLoading" :lines="20" class="loading" />
    <template v-else>
      <p v-if="$screen.isTabletOrMobile($mq)">
        Workflow management is only available on desktop sized screens.
      </p>
      <template v-else>
        <div class="meta-options">
          <dropdown v-if="hasEntitiesWithWorkflow" placement="bottom-end" class="entities-selector">
            <span>{{entitiesWithWorkflowText}}</span>&nbsp;
            <i class="ddi ddi-caret-down" />
            <template #content>
              <div>
                <ul>
                  <li v-for="entity in entitiesWithWorkflow" :key="entity.id" @click="sendToEntityHome(entity)">
                    <i :class="'ddi-' + entityTypeNameSingular.toLowerCase()" class="ddi" />
                    <span>{{entity.title}}</span>
                  </li>
                </ul>
              </div>
            </template>
          </dropdown>
          <template v-if="workflow.isPublished">
            <new-mailbox-button v-if="isForMailbox" :workflow-id="workflowID" size="small" />
            <new-project-button v-else :workflow-id="workflowID" size="small" />
          </template>
          <dd-button
            v-if="workflow.isPublishable"
            :is-loading="isPublishing"
            :color="btnColor"
            type="flat"
            size="small"
            @click="publishWorkflow()"
          >Publish</dd-button>
          <more-button-dropdown class="dropdown-options">
            <template #options-list>
              <ul>
                <li v-if="isCloneable" @click="cloneWorkflow()">
                  <i class="ddi ddi-workflows" />
                  <span>Clone&hellip;</span>
                  <i v-if="isCloning" class="ddi ddi-spinner" />
                </li>
                <li v-if="canUnpublishWorkflow" @click="unpublishWorkflow()">
                  <i class="ddi ddi-workflows" />
                  <span>Unpublish</span>
                </li>
                <li v-if="isWorkflowDeleteable" class="scary-list-option" @click="handleDeleteWorkflow()">
                  <i class="ddi ddi-trash" />
                  <span>Delete</span>
                </li>
              </ul>
            </template>
          </more-button-dropdown>
        </div>
        <div v-if="isBuiltIn" class="ribbon built-in">
          This is a built-in workflow. You can't edit it.
        </div>
        <div v-if="!workflow.isPublished" class="ribbon draft">
          This workflow is not published.
        </div>
        <div class="workflow-detail__main">
          <div class="workflow-detail__main--header">
            <div v-if="isBuiltIn || !showEditNameAndDescription" class="view" @click="showEditNameAndDescription=true">
              <h2>
                <span>{{workflow.name}}</span>
              </h2>
              <p>
                {{workflow.description}}
              </p>
            </div>
            <div v-else class="edit">
              <div class="inputs">
                <el-input v-model="editableWorkflowName"  maxlength="100" placeholder="Workflow name" />
                <el-input v-model="editableWorkflowDescription" maxlength="100" placeholder="Workflow description" />
              </div>
              <div class="buttons">
                <dd-button
                  :is-disabled="disableNameAndDescriptionUpdate"
                  :is-loading="isSubmittingNameAndDescription" 
                  :color="btnColor" 
                  size="tiny" 
                  @click="editNameAndDescription()"
                >Update</dd-button>
                <dd-button
                  size="tiny" 
                  type="flat"
                  @click="showEditNameAndDescription=false"
                >Cancel</dd-button>
              </div>
            </div>
          </div>
          <div class="workflow-detail__main--body">
            <template v-if="allowJobsManagement">
              <el-tooltip v-if="shouldSaveWorkflow">
                <template #content>
                  <div>Save your status changes first before you manage jobs.</div>
                </template>
                <div
                  v-if="allowJobsManagement" 
                  :class="{ 'has-jobs' : hasJobs }" 
                  class="manage-jobs-button disabled"
                >
                  <i class="ddi ddi-hammer" />
                  <template v-if="hasJobs">
                    <span>Manage jobs &rarr;</span>
                  </template>
                  <template v-else>
                    <span>Auto-assign {{ itemTypeNamePlural.toLowerCase() }} based on status? Add jobs! &rarr;</span>
                  </template>
                </div>
              </el-tooltip>
              <div
                v-else
                :class="{ 'has-jobs' : hasJobs }" 
                class="manage-jobs-button"
                @click="showManageJobsDrawer=true"
              >
                <i class="ddi ddi-hammer" />
                <template v-if="hasJobs">
                  <span>Manage jobs &rarr;</span>
                </template>
                <template v-else>
                  <span>Auto-assign {{ itemTypeNamePlural.toLowerCase() }} based on status? Add jobs! &rarr;</span>
                </template>
              </div>
            </template>
            <!-- Statuses -->
            <div class="statuses-manager">
              <div class="drag-container">
                <draggable
                  :animation="180"
                  :scroll-sensitivity="100"
                  :scroll-speed="20"
                  :force-fallback="true"
                  :list="statuses"
                  ghost-class="ghost-card"
                  drag-class="drag-card"
                  handle=".handle"
                  :item-key="positionKey"
                >
                  <template #item="{ element: status, index }">
                    <workflow-status-item
                      v-model:selected-job-id="status.assigneeMandateJobID"
                      :jobs="jobs"
                      :entity-type="entityType"
                      :index="index"
                      :status="status"
                      :read-only="isBuiltIn"
                      :show-delete="hasOverMinimumRequiredStatuses && (status.id === null || workflow.canDeleteStatuses)"
                      :show-settings="!isBuiltIn && status.id !== undefined"
                      :focus-on-create="isPostInitialLoad"
                      show-drag
                      @settings-clicked="(statusID: number) => showStatusTransitions(statusID)"
                      @delete-clicked="(index: number) => removeStatus(index)"
                    />
                  </template>
                </draggable>
                <add-workflow-status-item
                  v-if="!isBuiltIn && statuses.length < maxStatusesAllowed" 
                  :entity-type="entityType"
                  @clicked="addAdditionalStatus"
                />
                <div class="footer-buttons">
                  <dd-button
                    v-if="!disableSaveWorkflowButton"
                    type="flat"
                    @click="cancelSaveWorkflow()"
                  >Cancel</dd-button>
                  <dd-button
                    :is-loading="isModifyingStatuses"
                    :is-disabled="disableSaveWorkflowButton"
                    :color="btnColor"
                    type="flat"
                    @click="saveWorkflow(true)"
                  >Update Statuses</dd-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- Status transitions modal -->
      <workflow-status-transitions-modal
        v-model:show-modal="showStatusTransitionsModal"
        :workflow="workflow"
        :current-status-id="selectedStatusIDForStatusTransitionUpdate"
        @status-transitions-updated="handleStatusTransitionUpdate()"
      />
      <!-- Manage jobs drawer -->
      <manage-jobs-drawer
        v-model:show-drawer="showManageJobsDrawer"
        :workflow-id="workflow.id"
        :jobs="jobs"
        :assignee-mandates="assigneeMandates"
        :entity-type="entityType"
        @jobs-updated="handleJobsUpdate()" />
    </template>
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import TextAssistant from '@/helpers/text-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import WorkflowsAssistant from '@/helpers/workflows-assistant'

/* Data types */
import {
  IJob,
  IJobAssigneeMandate,
  IProjectWithAccessType,
  IWorkflowDetail,
} from '@/api/types/response.types'
import {
  IWorkflowsInputEditNameAndDescription,
  IWorkflowsInputModifyStatuses,
  IWorkflowsInputModifyStatusesStatus,
} from '@/api/types/request.types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'
import { EEntities } from '@/types'

export default defineComponent({
  name: 'WorkflowDetail',

  components: {
    CardCanvas,
    Draggable,
  },

  async beforeRouteLeave(to, from, next) {
    if (this.shouldSaveWorkflow) {
      try {
        await this.confirmSave()
        const success = await this.saveWorkflow()
        if (success) {
          next()
        }
      } catch {
        next()
      }
    } else {
      next()
    }
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      TextAssistant,
      TypeAssistant,
      WorkflowsAssistant,
      isWorkflowDeleteable: false,
      canUnpublishWorkflow: false,
      isInitialLoading: true,
      isLoading: true,
      isCloning: false,
      isDeleting: false,
      isUnpublishing: false,
      isPublishing: false,
      isModifyingStatuses: false,
      isSubmittingNameAndDescription: false,
      isPostInitialLoad: false,
      showEditNameAndDescription: false,
      shouldSaveWorkflow: false,
      workflow: {} as IWorkflowDetail,
      entitiesWithWorkflow: [] as IProjectWithAccessType[],
      statuses: [] as IWorkflowsInputModifyStatusesStatus[],
      jobs: [] as IJob[],
      assigneeMandates: [] as IJobAssigneeMandate[],

      editableWorkflowName: '',
      editableWorkflowDescription: '',

      showStatusTransitionsModal: false,
      selectedStatusIDForStatusTransitionUpdate: null as any,

      maxStatusesAllowed: 40,

      enableJobs: false,
      showManageJobsDrawer: false,
    }
  },

  head(): any {
    return {
      title: this.workflow.name,
    }
  },

  computed: {
    workflowID(): number {
      return Number(this.$route.params.id)
    },
    isBuiltIn(): boolean {
      return !this.workflow.isEditable
    },
    isCloneable(): boolean {
      return WorkflowsAssistant.isCloneable(this.workflow.name)
    },
    hasOverMinimumRequiredStatuses(): boolean {
      return this.statuses.length - 1 >= 2
    },
    disableSaveWorkflowButton(): boolean {
      return !this.shouldSaveWorkflow
    },
    disableNameAndDescriptionUpdate(): boolean {
      return this.editableWorkflowName.length === 0
    },
    hasEntitiesWithWorkflow(): boolean {
      return this.entitiesWithWorkflow.length > 0
    },
    entitiesWithWorkflowText(): string {
      const entityTypeName =
        this.entitiesWithWorkflow.length === 1
          ? this.entityTypeNameSingular.toLowerCase()
          : this.entityTypeNamePlural.toLowerCase()

      return `${
        this.entitiesWithWorkflow.length
      } ${entityTypeName} using this workflow`
    },
    isForMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    entityType(): EEntities {
      return TypeAssistant.getEntityType(this.workflow.entityTypeID)
    },
    entityTypeNameSingular(): string {
      return TextAssistant.entitySingularName(this.entityType)
    },
    entityTypeNamePlural(): string {
      return TextAssistant.entityPluralName(this.entityType)
    },
    itemTypeNameSingular(): string {
      return TextAssistant.itemSingularName(this.entityType)
    },
    itemTypeNamePlural(): string {
      return TextAssistant.itemPluralName(this.entityType)
    },
    btnColor(): string {
      return ColorAssistant.getEntityColorByEntityTypeID(
        this.workflow.entityTypeID,
      )
    },
    hasJobs(): boolean {
      return this.jobs.length > 0
    },
    allowJobsManagement(): boolean {
      return this.$permissions.jobsFeatureEnabled && !this.isBuiltIn
    },
  },

  watch: {
    statuses: {
      deep: true,
      handler() {
        if (!this.isLoading) {
          this.shouldSaveWorkflow = true
        }
      },
    },
    showStatusTransitionsModal: {
      immediate: false,
      handler(val) {
        if (!val) {
          this.selectedStatusIDForStatusTransitionUpdate = null
        }
      },
    },
  },

  async created() {
    this.isInitialLoading = true
    await this.fetchAllData()
    this.isInitialLoading = false
    await this.$nextTick()
    this.isPostInitialLoad = true
  },

  methods: {
    /* New statuses have no ID, so the draggable list keys them by position */
    positionKey(status: IWorkflowsInputModifyStatusesStatus): number {
      return this.statuses.indexOf(status)
    },

    async confirmSave() {
      await this.$confirm(`You've modified some statuses for this workflow.`, {
        dangerouslyUseHTMLString: true,
        center: true,
        confirmButtonText: 'Save updates',
        confirmButtonClass: 'el-button--primary',
        cancelButtonText: 'Discard changes',
        cancelButtonClass: 'el-button--cancel',
        type: 'warning',
        title: `Save these updates?`,
      })
    },
    removeStatus(index: number) {
      this.statuses.splice(index, 1)
    },
    addAdditionalStatus() {
      this.statuses.push({
        id: null,
        name: '',
        color: ColorAssistant.DefaultStatusColorOptions,
        isInactive: false,
        assigneeMandateJobID: this.hasJobs ? this.jobs[0].id : null,
      })
    },
    showStatusTransitions(statusID: number) {
      this.selectedStatusIDForStatusTransitionUpdate = statusID
      this.$nextTick(() => {
        this.showStatusTransitionsModal = true
      })
    },
    cancelSaveWorkflow() {
      this.shouldSaveWorkflow = false
      this.goToWorkflowsHome()
    },
    resetSaveDataCheck() {
      this.shouldSaveWorkflow = false
    },
    async handleStatusTransitionUpdate() {
      await this.refreshStatusTransitions()
    },
    goToWorkflowsHome() {
      RouteAssistant.goToWorkflowsHome(
        this.$router,
        this.$route.params.accountId,
      )
    },
    getJobForStatusID(statusID: number) {
      const mandate = this.assigneeMandates.find(am => am.statusID === statusID)

      if (mandate) {
        return mandate.jobID
      }

      return null
    },
    async handleJobsUpdate() {
      await this.getDetail()
      this.shouldSaveWorkflow = false
    },
    async fetchAllData() {
      this.isLoading = true
      await Promise.all([this.getDetail(), this.getEntitiesOnWorkflow()])
      this.isLoading = false
    },
    /* Returns success boolean for handling on beforeRouteLeave() */
    async saveWorkflow(showSuccess: boolean = false): Promise<boolean> {
      this.shouldSaveWorkflow = false

      try {
        this.isModifyingStatuses = true

        const data = {
          listStatuses: this.statuses,
        } as IWorkflowsInputModifyStatuses

        const response = await WorkflowsAPI.put_ModifyStatuses(
          this.workflow.id,
          data,
        )
        if (showSuccess) {
          this.$messaging.showSuccess(response.message)
          await this.fetchAllData()
        }
        return true
      } catch (err) {
        this.$error.emit(err, true)
        return false
      } finally {
        this.isModifyingStatuses = false
      }
    },
    async getDetail() {
      try {
        const response = await WorkflowsAPI.get_Detail(this.workflowID)
        this.workflow = response.detail
        this.isWorkflowDeleteable = response.isDeleteable
        this.canUnpublishWorkflow = response.canUnpublish

        this.editableWorkflowName = response.detail.name
        this.editableWorkflowDescription = response.detail.description

        this.resetSaveDataCheck()

        this.statuses = [] as IWorkflowsInputModifyStatusesStatus[]

        this.workflow.listStatuses.forEach(s => {
          const curAssigneeMandate = this.workflow.listAssigneeMandates.find(
            am => am.statusID === s.id,
          )
          this.statuses.push({
            id: s.id,
            name: s.name,
            color: s.color,
            isInactive: this.workflow.listInactiveStatuses.some(
              as => as.id === s.id,
            ),
            assigneeMandateJobID: curAssigneeMandate
              ? curAssigneeMandate.jobID
              : null,
          })
        })

        this.assigneeMandates = this.workflow.listAssigneeMandates
        this.jobs = this.workflow.listJobs
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async refreshStatusTransitions() {
      try {
        const response = await WorkflowsAPI.get_Detail(this.workflowID)
        this.workflow.listStatusTransitions =
          response.detail.listStatusTransitions
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async getEntitiesOnWorkflow() {
      try {
        this.entitiesWithWorkflow = await WorkflowsAPI.get_Projects(
          this.workflowID,
        )
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async cloneWorkflow() {
      try {
        this.isCloning = true
        const response = await WorkflowsAPI.post_Clone(this.workflow.id)
        RouteAssistant.goToWorkflowDetail(
          this.$router,
          this.$route.params.accountId,
          response.id,
        )
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isCloning = false
      }
    },
    async handleDeleteWorkflow() {
      // If deleting, undo the need to save...
      this.resetSaveDataCheck()

      await this.$confirm(
        `<strong>This cannot be undone.</strong>
        <br>Are you sure you want to delete this?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete this workflow?',
        },
      )
      await this.deleteWorkflow()
      this.goToWorkflowsHome()
    },
    async deleteWorkflow() {
      try {
        this.isDeleting = true
        const response = await WorkflowsAPI.delete_Delete(this.workflow.id)
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isDeleting = false
      }
    },
    async unpublishWorkflow() {
      this.resetSaveDataCheck()
      try {
        this.isUnpublishing = true
        await WorkflowsAPI.put_Unpublish(this.workflow.id)
        this.goToWorkflowsHome()
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isUnpublishing = false
      }
    },
    async publishWorkflow() {
      try {
        this.isPublishing = true
        if (this.shouldSaveWorkflow) {
          this.saveWorkflow()
        }
        await WorkflowsAPI.put_Publish(this.workflow.id)
        this.goToWorkflowsHome()
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isPublishing = false
      }
    },
    async editNameAndDescription() {
      try {
        this.isSubmittingNameAndDescription = true
        const data = {
          name: this.editableWorkflowName,
          description: this.editableWorkflowDescription,
        } as IWorkflowsInputEditNameAndDescription
        const response = await WorkflowsAPI.put_EditNameAndDescription(
          this.workflow.id,
          data,
        )
        this.workflow.name = this.editableWorkflowName
        this.workflow.description = this.editableWorkflowDescription
        this.showEditNameAndDescription = false
        this.$messaging.showSuccess(response.message)
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isSubmittingNameAndDescription = false
      }
    },
    sendToEntityHome(entity: IProjectWithAccessType) {
      if (this.isForMailbox) {
        RouteAssistant.goToMailbox(
          this.$router,
          this.$route.params.accountId,
          entity.id,
        )
      } else {
        RouteAssistant.goToProjectLanding(
          this.$router,
          this.$route.params.accountId,
          entity.id,
          this.$permissions,
        )
      }
    },
  },
})
</script>

<style lang="scss">
.workflow-detail {
  $name-and-desc-padding: 15px;

  &.view-only {
    .footer-buttons {
      display: none !important;
    }
    .view:hover {
      background-color: transparent;
    }
  }

  .meta-options {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(6px);
    position: absolute;
    top: 20px;
    right: 30px;

    .entities-selector {
      padding: 4px 10px;
      @include lettering--p-sm;
      border-radius: 20px;
      background-color: var(--color-background-secondary);
      border: $border--solid-light;
    }

    .dropdown-options {
      .ddi-spinner {
        margin-left: auto;
      }
    }
  }

  .ribbon {
    text-align: center;
    display: block;
    color: $color--white;
    padding: 2px 10px;
    position: absolute;
    background-color: $color--gray-75;
    border-top-right-radius: 8px;
    border-top-left-radius: 8px;
    top: -15px;
    left: 50%;
    width: 100%;
    transform: translateX(-50%);
    @include lettering--h5--no-color;
    @include standard-box-shadow;

    &.built-in {
      background-color: $color--gray-75;
    }

    &.draft {
      background-color: var(--color-text-third);
    }
  }

  &__main {
    display: flex;
    flex-direction: column;

    p + h4 {
      margin-top: 0;
    }

    h4 {
      margin-bottom: 10px;
    }

    &--header {
      display: flex;
      @include flex-row-horizontal-spacing(40px + $name-and-desc-padding);

      > :first-child {
        flex-grow: 1;
      }

      .view {
        margin: -1 * $name-and-desc-padding;
        padding: $name-and-desc-padding;
        border-radius: 8px;

        &:hover {
          background-color: var(--color-background-secondary);
        }

        > h2 {
          margin-bottom: 0;
        }
      }

      .edit {
        width: 100%;
        display: flex;
        @include flex-row-horizontal-spacing(10px);

        > * {
          flex-grow: 1;

          &.inputs {
            display: flex;
            flex-direction: column;
            @include flex-column-vertical-spacing(4px);
          }

          &:last-child {
            flex-grow: 0;
            margin-right: auto;
          }
        }

        .buttons {
          display: flex;
          align-items: center;
          @include flex-row-horizontal-spacing(4px);
        }
      }
    }

    &--body {
      position: relative;
      border-top: $border--dotted-light;
      margin-top: 20px;
      padding-top: 30px;

      .manage-jobs-button {
        border: $border--solid-light;
        border-radius: 20px;
        background-color: $color--gray-75;
        padding: 4px 10px;
        position: absolute;
        top: 10px;
        right: 0;
        min-width: 150px;
        @include lettering--p-sm;
        color: $color--white;
        font-weight: $bold;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        @include flex-row-horizontal-spacing(4px);

        &.has-jobs {
          right: 54px;
        }

        &.disabled {
          opacity: 0.7;
          cursor: default;
        }

        &:hover {
          background-color: $color--gray-90;
        }
      }

      .statuses-manager {
        margin-top: 20px;
      }
    }

    .drag-container {
      .ghost-card {
        cursor: grab !important;
        opacity: 0.5 !important;
        > * {
          visibility: hidden;
        }
      }

      .drag-card {
        cursor: grab !important;
        transform: rotate(-0.4deg);
        z-index: 2000;
      }
    }

    .footer-buttons {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
      @include flex-row-horizontal-spacing(6px);
    }
  }
}
</style>
